/**
 * SpecialtyPicker — composes LookupPicker for provider_specialties
 * (Block 6.B.2).
 */

import { useCallback, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { canAddLookup, lookupsApi } from '@/lib/lookups';
import type { LookupRow } from '@/lib/types';
import { LookupPicker } from './LookupPicker';

export interface SpecialtyPickerProps {
  providerId: string;
  selected: LookupRow[];
  onMutated: () => Promise<void>;
  /** Phase 1.11 — read-only render when false. Defaults to true. */
  canEdit?: boolean;
}

export function SpecialtyPicker({
  providerId,
  selected,
  onMutated,
  canEdit = true,
}: SpecialtyPickerProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();

  const loadOptions = useCallback(
    async (q: string, limit: number): Promise<LookupRow[]> => {
      const r = await lookupsApi.listSpecialties(tenantSlug, { q, limit });
      return r.rows;
    },
    [tenantSlug],
  );

  const onAdd = useCallback(
    async (slug: string): Promise<void> => {
      await lookupsApi.addProviderSpecialization(tenantSlug, providerId, { slug });
      await onMutated();
    },
    [tenantSlug, providerId, onMutated],
  );

  const onRemove = useCallback(
    async (specialtyId: string): Promise<void> => {
      await lookupsApi.removeProviderSpecialization(tenantSlug, providerId, specialtyId);
      await onMutated();
    },
    [tenantSlug, providerId, onMutated],
  );

  const onCreate = useCallback(
    async (displayName: string): Promise<{ slug: string; display_name: string }> => {
      const created = await lookupsApi.addSpecialty(tenantSlug, { display_name: displayName });
      return { slug: created.slug, display_name: created.display_name };
    },
    [tenantSlug],
  );

  return (
    <LookupPicker<LookupRow>
      selected={selected}
      getLabel={(item) => item.display_name}
      getId={(item) => item.id}
      getSlug={(item) => item.slug}
      loadOptions={loadOptions}
      onAdd={onAdd}
      onRemove={onRemove}
      seeding={{
        canAdd: canAddLookup(whoami),
        onCreate,
        typeLabel: 'specialty',
      }}
      label="specialty"
      placeholder="Search specialties"
      chipVariant="specialty"
      loadOptionsLimit={200}
      canEdit={canEdit}
    />
  );
}
